import {UI} from "../../stem-core/ui/UIBase";
import {DashboardTablePage, MakeEmailEntry} from "../common/DashboardTablePage";
import {
    apiMerchantDeleteLogMessage,
    MerchantLogMessage
} from "../../client/state/merchant/MerchantLogMessageStore";
import {LoggingLevel} from "../../client/state/misc/BaseLogMessage.js";
import {LEFT_COLUMN} from "../common/theme/TableStyle";
import {DashboardStaticText} from "../common/DashboardStaticText";
import {CollapsibleTable} from "../../stem-core/ui/table/CollapsibleTable";
import {MerchantUser} from "../../client/state/merchant/MerchantUserStore";
import {DashboardLabel} from "../common/DashboardLabel";
import {BlinkInputField} from "../common/Input";
import {Select, TextInput} from "../../stem-core/ui/input/Input";
import {ConfirmationModal} from "../../blinkpay/ui/ConfirmationModal";
import {SearchInput} from "../ui/input/SearchInput.jsx";
import {UserSearchInput} from "../ui/input/UserSearchInput.jsx";
import {TrashCanIcon} from "../SVGElements";
import {Modal} from "../../blinkpay/ui/Modal.jsx";
import {apiStreamQuery} from "../../client/connection/BlinkApiClient.js";
import {TaskProgressStatusDescriptor} from "./tnr/TaskProgressStatusDescriptor.jsx";
import {BaseTaskProgressStatus} from "./tnr/BaseTaskProgressStatus.jsx";
import {CSVBuilder} from "../../stem-core/base/CSV.js";
import {capitalize, isString} from "../../stem-core/base/Utils.js";
import {Button} from "../../stem-core/ui/button/Button.jsx";
import {DashboardTitle} from "../common/DashboardTitle.js";
import type {ColumnTuple} from "../../stem-core/base/ColumnHandler";


interface LogMessageExtraOptions {
    message?: MerchantLogMessage;
}

class LogMessageExtra extends UI.Element<LogMessageExtraOptions> {
    render() {
        const {message} = this.options;
        const {extra} = message;
        if (!extra) {
            return "No extra information to show";
        }
        return <DashboardStaticText type="json" value={extra} />
    }
}


// One page of /merchant/get_log_messages, as apiStreamQuery yields them
interface LogMessagesResponse {
    state: {MerchantLogMessage: Record<string, any>[]};
    count: number;
}

interface LogStreamDownloadModalOptions {
    // The stream-processing handle, not a status string
    status?: BaseTaskProgressStatus;
    stream?: AsyncIterable<LogMessagesResponse>;
}

class LogStreamDownloadModal extends Modal<LogStreamDownloadModalOptions> {
    entries: Record<string, any>[] = [];

    downloadCSV() {
        const columns = [];
        const identifiedColumns = new Set();
        for (const entry of this.entries) {
            for (const key of Object.keys(entry)) {
                if (identifiedColumns.has(key)) {
                    continue;
                }
                identifiedColumns.add(key);
                columns.push([capitalize(key), (obj: Record<string, any>) => {
                    const value = obj[key];
                    if (value == null) {
                        return "";
                    }
                    if (isString(value)) {
                        return value;
                    }
                    return JSON.stringify(value);
                }])
            }
        }
        CSVBuilder.saveFile(columns, this.entries);
    }

    render() {
        const {status} = this.options;

        return [
            <DashboardTitle
                title="Download log messages"
                style={{marginBottom: 12}}
            />,
            <TaskProgressStatusDescriptor style={{maxWidth: "100%", margin: "auto"}} status={status} />,
            <div style={{textAlign: "center"}}>
                <Button label="Download CSV" onClick={() => this.downloadCSV()} />
            </div>
        ]
    }

    hide() {
        this.options.status.setStop();
        super.hide();
    }

    async onMount() {
        super.onMount();

        const {stream, status} = this.options;

        await status.processStream(stream, (response: LogMessagesResponse) => {
            const rawLogMessages = response.state.MerchantLogMessage;
            for (const message of rawLogMessages) {
                this.entries.push({
                    id: message.id,
                    time: message.createdAt,
                    level: message.level,
                    code: message.code,
                    userId: message.userId,
                    // userEmail: message.user.email,
                    message: message.message,
                    ...message.extra,
                })
            }
            this.redraw();
            return [rawLogMessages.length, response.count];
        });
    }
}


export class MerchantLoggingPage extends DashboardTablePage<MerchantLogMessage> {
    declare codeInput: TextInput;
    declare messageInput: SearchInput;
    declare minLevelInput: Select<any>;
    declare userSearchInput: UserSearchInput;

    getDefaultOptions() {
        return {
            title: "Logging messages",
            store: MerchantLogMessage,
            endpoint: "/merchant/get_log_messages",
        }
    }

    async deleteMerchantMessage(logMessage: MerchantLogMessage) {
        const {message} = logMessage;
        const confirm = await ConfirmationModal.prompt({
            title: "Delete error message?",
            description: [<div>Are your sure you want to delete this error message?
                <p><i>{message}</i></p>
            </div>],
            confirmLabel: "Delete log message"
        });
        if (!confirm) {
            return;
        }
        await apiMerchantDeleteLogMessage({messageId: logMessage.id});
        await this.paginator.fetchPage();
    }

    getFilterSection() {
        const applyFilters = () => this.applyFilters();

        return <div>
            <BlinkInputField label="Min Level">
                <Select
                    options={[LoggingLevel.ERROR, LoggingLevel.WARNING, "Any"]}
                    initialValue={LoggingLevel.WARNING}
                    onChange={applyFilters}
                    ref="minLevelInput"
                />
            </BlinkInputField>

            <BlinkInputField label={"Code"}>
                {/*// TODO @Mihai make this multi input*/}
                <TextInput ref="codeInput" onChange={applyFilters} />
            </BlinkInputField>

            <UserSearchInput onChange={applyFilters} ref="userSearchInput"/>

            <BlinkInputField label={"Message text"}>
                <SearchInput inputAttributes={{placeholder:"Case insensitive search"}} ref="messageInput" onChange={applyFilters} />
            </BlinkInputField>
        </div>
    }

    getFilters() {
        let filters: Record<string, any> = {
            minLevel: this.minLevelInput.getValue().value,
            userFilter: this.userSearchInput.getValue(),
            messageText: this.messageInput.getValue().trim() || null,
        };

        const code = this.codeInput.getValue();
        if (code && parseInt(code)) {
            filters.code = [parseInt(code)];
        } else {
            filters.code = null; // Clear it
        }

        return filters;
    }

    exportData() {
        const status = new BaseTaskProgressStatus();
        const stream = apiStreamQuery(this.options.endpoint, this.getFilters(), {status});

        LogStreamDownloadModal.show({
            stream,
            status,
        })
    }

    entriesTable() {
        // TODO @Mihai only show the collapsible icon when there is extra info
        const table = super.entriesTable();
        Object.assign(table.options, {
            tableClass: CollapsibleTable,
            exportCallback: () => this.exportData(),
            tableOptions: {
                renderCollapsible: (message: MerchantLogMessage) => <LogMessageExtra message={message} />,
            }
        });
        return table;
    }

    getColumns(): ColumnTuple<MerchantLogMessage>[] {
        const {merchant} = this.options;
        return [
            ["Date", message => message.createdAt, LEFT_COLUMN],
            ["Level", message => {
                const {level} = message;
                return <DashboardLabel level={level.getLabelLevel()}>{level}</DashboardLabel>
            }, LEFT_COLUMN],
            ["User", message => {
                const {userId} = message;
                if (!userId) {
                    return null;
                }
                const merchantUser = MerchantUser.getByUserAndMerchant(userId, merchant.id);
                if (!merchantUser) {
                    return `UserId ${userId}`;
                }
                return MakeEmailEntry(merchantUser);
            }, LEFT_COLUMN],
            ["Code", message => message.code, LEFT_COLUMN],
            ["Message", message => message.message, LEFT_COLUMN],
            ["", message => <TrashCanIcon style={{cursor: "pointer", verticalAlign: "middle"}} size={24}  onClick={() => this.deleteMerchantMessage(message)} />]
        ]
    }
}
